import React, { useState } from 'react';
import { Truck, User, Phone, Mail, MapPin, FileText, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { vehicleTypes } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const JoinFleetSection = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ownerName: '',
    phone: '',
    email: '',
    vehicleType: '',
    vehicleNumber: '',
    city: '',
    experience: '',
    additionalInfo: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/fleet/register`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          ownerName: '',
          phone: '',
          email: '',
          vehicleType: '',
          vehicleNumber: '',
          city: '',
          experience: '',
          additionalInfo: ''
        });
      }
    } catch (error) {
      console.error('Fleet registration error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="join-fleet" className="relative flex items-center justify-center bg-gradient-to-b from-white to-blue-50 overflow-hidden py-20">
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
        <h1 className="text-[5rem] md:text-[12rem] font-bold text-gray-900 select-none">
          PRIMESWIFT
        </h1>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-block mb-4 px-4 py-2 bg-green-100 rounded-full">
              <span className="text-green-700 text-sm font-semibold">JOIN OUR NETWORK</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Become a Fleet Partner
            </h2>
            <p className="text-base md:text-xl text-gray-600 mb-8 leading-relaxed">
              Expand your business opportunities by joining Primeswift's growing logistics network. 
              Get consistent loads, timely payments, and professional support.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              {[
                { icon: '💰', title: 'Competitive Rates', desc: 'Industry-leading payment terms' },
                { icon: '📦', title: 'Regular Loads', desc: 'Consistent business opportunities' },
                { icon: '⏱️', title: 'Timely Payments', desc: 'No delays, guaranteed payments' },
                { icon: '🤝', title: 'Dedicated Support', desc: '24/7 partner assistance' }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-3xl">{benefit.icon}</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{benefit.title}</h4>
                    <p className="text-sm text-gray-600">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Registration Form */}
          <Card className="shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center">
                <Truck className="mr-2 h-6 w-6 text-blue-600" />
                Fleet Registration Form
              </CardTitle>
              <CardDescription>
                Fill in your details and we'll get back to you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="ownerName">
                    <User className="inline h-4 w-4 mr-1" />
                    Owner Name *
                  </Label>
                  <Input
                    id="ownerName"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="vehicleType">
                    <Truck className="inline h-4 w-4 mr-1" />
                    Vehicle Type *
                  </Label>
                  <Select
                    value={formData.vehicleType}
                    onValueChange={(value) => setFormData({ ...formData, vehicleType: value })}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vehicleNumber">Vehicle Number</Label>
                    <Input
                      id="vehicleNumber"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleChange}
                      placeholder="MH-01-XX-1234"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      City *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Your city"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience">Years of Experience</Label>
                  <Input
                    id="experience"
                    name="experience"
                    type="number"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Years in logistics"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="additionalInfo">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Additional Information
                  </Label>
                  <Textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder="Tell us more about your fleet..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg group"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
